// Comparison-period presets for reports. Given a current period (asOf for BS,
// from+to for IS/CF), compute the corresponding compare-to period for each
// preset. The dropdown on each report page picks one of these.
//
// Date math is calendar-month aware (not 30-day approximations) so "last
// quarter" off a Feb 28 picks Nov 28, not Nov 30.

export type ComparePreset =
  | 'none'
  | 'prior_period'
  | 'last_month'
  | 'last_quarter'
  | 'last_year'
  | 'ytd_last_year'
  | 'ttm';

export const COMPARE_PRESET_LABELS: Record<ComparePreset, string> = {
  none: 'None',
  prior_period: 'Prior period (same length)',
  last_month: 'Last month',
  last_quarter: 'Last quarter',
  last_year: 'Last year (same period)',
  ytd_last_year: 'YTD last year',
  ttm: 'Trailing 12 months',
};

/** A period — for a Balance Sheet (asOf only) or an Income Statement / Cash Flow (from + to). */
export interface ComparePeriod {
  asOf?: string;
  from?: string;
  to?: string;
  /** Short label for the column header (e.g. "Apr 2025"). */
  label: string;
}

function parseISO(iso: string): Date {
  // Parse as UTC so we don't drift across DST.
  return new Date(`${iso}T00:00:00Z`);
}

function toISO(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Shift a date by N calendar months. End-of-month clamps to the new month's last day. */
function shiftMonths(iso: string, months: number): string {
  const d = parseISO(iso);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const day = d.getUTCDate();
  const target = new Date(Date.UTC(year, month + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return toISO(target);
}

/** Days inclusive between two ISO dates (from <= to). */
function daysBetween(fromISO: string, toISO: string): number {
  const a = parseISO(fromISO);
  const b = parseISO(toISO);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function addDays(iso: string, days: number): string {
  const d = parseISO(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toISO(d);
}

/** Short label: "Apr 2025" or "Jan – May 2025". */
function shortLabel(period: { asOf?: string; from?: string; to?: string }): string {
  const fmt = (iso: string): string => {
    const d = parseISO(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };
  if (period.asOf) return fmt(period.asOf);
  if (period.from && period.to) {
    if (period.from.slice(0, 7) === period.to.slice(0, 7)) return fmt(period.to);
    return `${fmt(period.from)} – ${fmt(period.to)}`;
  }
  return '';
}

/**
 * Compute the comparison period from the current period and a preset.
 * Returns null when the preset doesn't apply (e.g. "TTM" on a Balance Sheet).
 */
export function computeComparePeriod(
  preset: ComparePreset,
  current: { asOf?: string; from?: string; to?: string },
): ComparePeriod | null {
  if (preset === 'none') return null;

  const isPointInTime = !!current.asOf;
  const isRange = !!current.from && !!current.to;

  if (isPointInTime) {
    // Balance Sheet — shift the asOf back by a calendar interval.
    const asOf = current.asOf!;
    switch (preset) {
      case 'prior_period': // same as last_month for BS — no real "period length" to mirror
      case 'last_month': {
        const c = shiftMonths(asOf, -1);
        return { asOf: c, label: shortLabel({ asOf: c }) };
      }
      case 'last_quarter': {
        const c = shiftMonths(asOf, -3);
        return { asOf: c, label: shortLabel({ asOf: c }) };
      }
      case 'last_year':
      case 'ytd_last_year': {
        const c = shiftMonths(asOf, -12);
        return { asOf: c, label: shortLabel({ asOf: c }) };
      }
      case 'ttm':
        // TTM doesn't apply to a point-in-time report; degrade to "last year".
        return computeComparePeriod('last_year', current);
      default:
        return null;
    }
  }

  if (isRange) {
    const from = current.from!;
    const to = current.to!;
    switch (preset) {
      case 'prior_period': {
        const lengthDays = daysBetween(from, to);
        const compareTo = addDays(from, -1);
        const compareFrom = addDays(compareTo, -lengthDays);
        return {
          from: compareFrom,
          to: compareTo,
          label: shortLabel({ from: compareFrom, to: compareTo }),
        };
      }
      case 'last_month': {
        const cFrom = shiftMonths(from, -1);
        const cTo = shiftMonths(to, -1);
        return { from: cFrom, to: cTo, label: shortLabel({ from: cFrom, to: cTo }) };
      }
      case 'last_quarter': {
        const cFrom = shiftMonths(from, -3);
        const cTo = shiftMonths(to, -3);
        return { from: cFrom, to: cTo, label: shortLabel({ from: cFrom, to: cTo }) };
      }
      case 'last_year': {
        const cFrom = shiftMonths(from, -12);
        const cTo = shiftMonths(to, -12);
        return { from: cFrom, to: cTo, label: shortLabel({ from: cFrom, to: cTo }) };
      }
      case 'ytd_last_year': {
        // Jan 1 of last year through same M-D as current `to`, one year back.
        const lastYear = parseISO(to).getUTCFullYear() - 1;
        const cFrom = `${lastYear}-01-01`;
        const cTo = shiftMonths(to, -12);
        return { from: cFrom, to: cTo, label: shortLabel({ from: cFrom, to: cTo }) };
      }
      case 'ttm': {
        // Same period length as current, but rolling back exactly 12 months.
        // i.e. the equivalent period from a year ago. (Distinct from prior_period.)
        const cFrom = shiftMonths(from, -12);
        const cTo = shiftMonths(to, -12);
        return { from: cFrom, to: cTo, label: `TTM ${shortLabel({ from: cFrom, to: cTo })}` };
      }
      default:
        return null;
    }
  }
  return null;
}

/** Format dollar delta with sign. */
export function formatMoneyDelta(current: string | number, prior: string | number): string {
  const c = typeof current === 'number' ? current : parseFloat(current);
  const p = typeof prior === 'number' ? prior : parseFloat(prior);
  if (!isFinite(c) || !isFinite(p)) return '';
  const delta = c - p;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const abs = Math.abs(delta);
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format percentage change with sign. Empty when prior is zero (undefined growth). */
export function formatPctChange(current: string | number, prior: string | number): string {
  const c = typeof current === 'number' ? current : parseFloat(current);
  const p = typeof prior === 'number' ? prior : parseFloat(prior);
  if (!isFinite(c) || !isFinite(p) || p === 0) return '';
  const pct = ((c - p) / Math.abs(p)) * 100;
  if (!isFinite(pct) || Math.abs(pct) > 99_999) return '';
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}
